import { useSyncExternalStore } from 'react';
import { IS_IN_EDITOR_FRAME } from '@vibesites/runtime/react';
import {
  getConsentState,
  subscribeConsent,
  type TrackingCategory,
} from './consent';

export type { TrackingCategory };

/**
 * Analytics vendors should only receive traffic from production deploys.
 * The editor iframe and Cloudflare preview deployments are separate concerns:
 * one is detected at runtime, the other is baked into the bundle at build time.
 */
export function useAnalyticsEnabled(): boolean {
  const env = (import.meta as unknown as {
    env?: { VIBESITES_DEPLOYMENT_ENVIRONMENT?: string };
  }).env;

  return !IS_IN_EDITOR_FRAME && env?.VIBESITES_DEPLOYMENT_ENVIRONMENT === 'production';
}

/**
 * Gates a tracker block on both the production/editor check AND the
 * visitor's opt-out state. Reactive — flips to `false` when the visitor
 * opts out through the `privacy-consent` block or when GPC is detected,
 * which unsubscribes tracker effects and prevents future script loads.
 *
 * `category` picks which bucket the caller belongs to:
 *   - `'advertising'`: Meta, Google Ads, GTM (can load arbitrary ad tags),
 *     TikTok, AdTaxi/Tealium, Basis, OpenAI ads pixel, image pixels, hy.ly
 *   - `'analytics'`: GA4, Clarity, CallRail
 *
 * A tracker in a given category is disabled when opt-out is active AND
 * that category is included in `categoriesGated` on the consent store.
 * By default only `'advertising'` is gated (matches GPC's legal scope);
 * a site can widen it to include `'analytics'` via the block's
 * `opt_out_scope` config.
 */
export function useTrackingAllowed(category: TrackingCategory): boolean {
  const analyticsEnabled = useAnalyticsEnabled();
  const consent = useSyncExternalStore(
    subscribeConsent,
    getConsentState,
    getConsentState,
  );

  if (!analyticsEnabled) return false;
  if (!consent.optedOut) return true;
  return !consent.categoriesGated.includes(category);
}
